import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const TOKEN_KEY = "infin_jwt";

export function getToken() {
  return localStorage.getItem(TOKEN_KEY);
}
export function setToken(t) {
  if (t) localStorage.setItem(TOKEN_KEY, t);
  else localStorage.removeItem(TOKEN_KEY);
}

const client = axios.create({ baseURL: API });
client.interceptors.request.use((cfg) => {
  const t = getToken();
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});

export async function register(data) {
  const r = await client.post("/auth/register", data);
  setToken(r.data.token);
  return r.data.user;
}
export async function login(data) {
  const r = await client.post("/auth/login", data);
  setToken(r.data.token);
  return r.data.user;
}
export async function changePassword(current_password, new_password) {
  return (await client.post("/auth/change-password", { current_password, new_password })).data;
}
export async function me() {
  return (await client.get("/auth/me")).data;
}
export async function listCases() {
  return (await client.get("/cases")).data;
}
export async function createCase() {
  return (await client.post("/cases")).data;
}
export async function getCase(id) {
  return (await client.get(`/cases/${id}`)).data;
}
export async function getCurrentWidget(id) {
  return (await client.get(`/cases/${id}/widget`)).data;
}
export async function getFilingProgress(id) {
  return (await client.get(`/cases/${id}/filing-progress`)).data;
}
export async function createStripeCheckout(id, origin) {
  return (await client.post(`/cases/${id}/create-order`, { origin })).data;
}
export async function getPaymentStatus(id, sessionId) {
  return (await client.get(`/cases/${id}/payment-status/${sessionId}`)).data;
}
export async function getChatHistory(id) {
  return (await client.get(`/cases/${id}/chat-history`)).data;
}
export async function sendChat(id, message) {
  return (await client.post(`/cases/${id}/chat`,
    { case_id: id, user_message: message })).data;
}
export async function uploadFile(caseId, file) {
  const form = new FormData();
  form.append("file", file);
  return (await client.post(`/cases/${caseId}/files`, form,
    { headers: { "Content-Type": "multipart/form-data" } })).data;
}
export async function uploadFilesBatch(caseId, files) {
  const form = new FormData();
  for (const f of files) form.append("files", f);
  return (await client.post(`/cases/${caseId}/files/batch`, form,
    { headers: { "Content-Type": "multipart/form-data" } })).data;
}
export function fileDownloadUrl(file_id) {
  return `${API}/files/${file_id}`;
}
export async function backofficeList(filters = {}) {
  // WO 6d — server-side filters. ``filters`` keys: status, priority,
  // entity_type, has_pending_response, updated_from, updated_to.
  const params = {};
  for (const [k, v] of Object.entries(filters)) {
    if (v === undefined || v === null || v === "") continue;
    params[k] = v;
  }
  return (await client.get("/backoffice/cases", { params })).data;
}
export async function backofficeGet(id) {
  return (await client.get(`/backoffice/cases/${id}`)).data;
}
export async function backofficeRecordManualVerification(
  caseId, stakeholderId, fileId, flagName, reason,
) {
  // WO 6c — per-flag admin sign-off on a HIGH authenticity flag.
  return (await client.post(
    `/backoffice/cases/${caseId}/manual-verification`,
    { stakeholder_id: stakeholderId, file_id: fileId,
      flag_name: flagName, reason },
  )).data;
}
export async function backofficeUpdateFilingOutcome(caseId, fields) {
  // WO 6b — admin writes the typed filing-outcome block.
  return (await client.post(
    `/cases/${caseId}/filing-outcome`, fields)).data;
}
export async function backofficeRevealAadhaar(caseId, stakeholderId) {
  return (await client.get(
    `/backoffice/cases/${caseId}/aadhaar/${stakeholderId}`)).data;
}
export async function transcribeAudio(caseId, blob) {
  const form = new FormData();
  // Hint Whisper with a sensible filename (the backend re-detects suffix).
  const filename = blob.type?.includes("mp4") ? "voice.mp4" : "voice.webm";
  form.append("audio", blob, filename);
  return (await client.post(`/cases/${caseId}/transcribe`, form,
    { headers: { "Content-Type": "multipart/form-data" } })).data;
}

// ---- DPDP: consent + retention + erasure ----
export async function getConsentNotice() {
  return (await client.get("/consent/notice")).data;
}
export async function recordConsent(caseId) {
  return (await client.post(`/cases/${caseId}/consent`, { agreed: true })).data;
}
export async function recordAuthenticityOverride(caseId, fileId, body) {
  // WO 6 — founder "use this anyway" affirm. body shape:
  //   { reason, stakeholder_id?, slot?, flag_names? }
  return (await client.post(
    `/cases/${caseId}/files/${fileId}/authenticity-override`, body,
  )).data;
}
export async function discardBlockedUpload(caseId, fileId) {
  // WO 6 — founder picked "Re-upload" on the affirm widget.
  return (await client.delete(
    `/cases/${caseId}/files/${fileId}/blocked-upload`,
  )).data;
}
export async function confirmEntityType(caseId, entity_type) {
  return (await client.post(
    `/cases/${caseId}/entity-type-confirm`, { entity_type })).data;
}
export async function attachInc3Consent(caseId, file_id, stakeholder_id) {
  return (await client.post(
    `/cases/${caseId}/inc3-consent`, { file_id, stakeholder_id })).data;
}
export async function getRetention(caseId) {
  return (await client.get(`/cases/${caseId}/retention`)).data;
}
export async function eraseCase(caseId) {
  return (await client.delete(`/cases/${caseId}`)).data;
}

// ---- Back office: KB + escalations + retention ----
export async function kbList(status) {
  const q = status ? `?status=${encodeURIComponent(status)}` : "";
  return (await client.get(`/backoffice/kb${q}`)).data;
}
export async function kbCreate(payload) {
  return (await client.post("/backoffice/kb", payload)).data;
}
export async function kbUpdate(id, payload) {
  return (await client.patch(`/backoffice/kb/${id}`, payload)).data;
}
export async function kbDelete(id) {
  return (await client.delete(`/backoffice/kb/${id}`)).data;
}
export async function escalationsList(openOnly = true) {
  return (await client.get(`/backoffice/escalations?open_only=${openOnly}`)).data;
}
export async function backofficeMetrics() {
  return (await client.get("/backoffice/metrics")).data;
}
export async function backofficeUsers() {
  return (await client.get("/backoffice/users")).data;
}
export async function backofficeUserDetail(userId) {
  return (await client.get(`/backoffice/users/${userId}`)).data;
}
export async function setFilingProgress(caseId, body) {
  return (await client.post(`/cases/${caseId}/filing-progress`, body)).data;
}
export async function answerEscalation(caseId, idx, answer, addToKb) {
  return (await client.post(
    `/backoffice/cases/${caseId}/escalations/${idx}/answer`,
    { answer, add_to_kb: !!addToKb })).data;
}

export default client;
